import { Link } from "react-router-dom";
import { Check, ArrowRight, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSiteContent } from "@/lib/useSiteContent";

/* ---- CTAs réutilisables : les 2 actions prioritaires du CDC ---- */
export function PrimaryCTA({ className = "", children = "Demander une étude gratuite" }) {
  return (
    <Button asChild size="lg" className={`rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold ${className}`}>
      <Link to="/contact?type=etude">{children}<ArrowRight className="h-4 w-4 ml-1" /></Link>
    </Button>
  );
}
export function SecondaryCTA({ className = "", children = "Déposer ma facture" }) {
  return (
    <Button asChild size="lg" variant="outline" className={`rounded-full px-7 border-primary/20 ${className}`}>
      <Link to="/contact?type=facture"><FileText className="h-4 w-4 mr-1" />{children}</Link>
    </Button>
  );
}

/* ---- Hero des pages intérieures ---- */
export function PageHero({ eyebrow, title, subtitle, withCtas = true, image, children }) {
  const content = (
    <>
      {eyebrow && <p className="em-eyebrow mb-4">{eyebrow}</p>}
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-primary leading-[1.07]">
        {title}
      </h1>
      {subtitle && <p className="text-lg text-muted-foreground mt-6 leading-relaxed">{subtitle}</p>}
      {children}
      <div className="flex flex-col sm:flex-row gap-3 mt-8">
        <SecondaryCTA />
      </div>
    </>
  );

  if (!image) {
    return (
      <section className="em-grain border-b border-border">
        <div className="em-container pt-16 md:pt-20 pb-14 md:pb-20 max-w-4xl">{content}</div>
      </section>
    );
  }

  return (
    <section className="em-grain border-b border-border">
      <div className="em-container pt-16 md:pt-20 pb-14 md:pb-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <div>{content}</div>
        <div className="rounded-3xl overflow-hidden ring-1 ring-border shadow-xl">
          <picture>
            <source srcSet={image.replace(/\.(png|jpe?g)$/i, ".webp")} type="image/webp" />
            <img
              src={image}
              alt=""
              loading="eager"
              decoding="async"
              className="w-full h-[300px] md:h-[440px] object-cover"
              onError={(e) => { e.currentTarget.parentElement.parentElement.style.display = "none"; }}
            />
          </picture>
        </div>
      </div>
    </section>
  );
}

/* ---- Bloc de réassurance (sous le hero) ---- */
const REASSURANCE = ["Étude gratuite", "Sans engagement", "Particuliers & professionnels", "Accompagnement national"];
export function Reassurance() {
  return (
    <section className="bg-primary text-primary-foreground">
      <div className="em-container py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {REASSURANCE.map((r) => (
          <div key={r} className="flex items-center gap-2 text-sm md:text-base font-medium">
            <Check className="h-5 w-5 text-accent shrink-0" /> {r}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---- Section générique ---- */
export function Section({ id, eyebrow, title, intro, children, muted = false, tight = false, className = "", cmsKey = "" }) {
  const c = useSiteContent(cmsKey ? [`v2.${cmsKey}.eyebrow`, `v2.${cmsKey}.title`, `v2.${cmsKey}.intro`] : []);
  if (cmsKey) {
    eyebrow = c(`v2.${cmsKey}.eyebrow`, eyebrow);
    title = c(`v2.${cmsKey}.title`, title);
    intro = c(`v2.${cmsKey}.intro`, intro);
  }
  const padding = tight ? "pt-20 md:pt-28 pb-10 md:pb-12" : "em-section";
  return (
    <section id={id} className={`${muted ? "bg-secondary" : ""} ${className}`}>
      <div className={`em-container ${padding}`}>
        {eyebrow && <p className="em-eyebrow mb-3">{eyebrow}</p>}
        {title && <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-primary max-w-3xl">{title}</h2>}
        {intro && <p className="text-muted-foreground mt-5 max-w-3xl leading-relaxed">{intro}</p>}
        {children && <div className="mt-10">{children}</div>}
      </div>
    </section>
  );
}

/* ---- Liste à puces stylée ---- */
export function Bullets({ items, columns = 1 }) {
  return (
    <ul className={`grid gap-3 ${columns === 2 ? "md:grid-cols-2" : ""}`}>
      {items.map((it) => (
        <li key={it} className="flex gap-3 text-muted-foreground">
          <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

/* ---- Étapes numérotées ---- */
export function Steps({ steps }) {
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {steps.map((s, i) => (
        <Card key={s.title || i} className="rounded-2xl border-border em-card-hover">
          <CardContent className="p-6">
            <span className="font-display text-4xl font-black text-muted-foreground/40">0{i + 1}</span>
            <h3 className="font-display text-lg font-bold text-primary mt-3">{s.title}</h3>
            {s.text && <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{s.text}</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TimelineStep({ step, index, className = "" }) {
  return (
    <Card className={`rounded-2xl border-border bg-card h-full ${className}`}>
      <CardContent className="p-6 h-full flex flex-col">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent font-display text-sm font-black text-accent-foreground">
          {String(index + 1).padStart(2, "0")}
        </span>
        <h3 className="font-display text-lg font-bold text-primary mt-4">{step.title}</h3>
        {step.text && <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{step.text}</p>}
      </CardContent>
    </Card>
  );
}

/* ---- Parcours connecté (4 étapes) ---- */
export function ProcessTimeline({ steps }) {
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2 xl:hidden">
        {steps.map((s, i) => (
          <TimelineStep key={s.title || i} step={s} index={i} />
        ))}
      </div>
      <div className="hidden xl:flex xl:items-stretch">
        {steps.map((s, i) => (
          <div key={s.title || i} className="flex items-stretch flex-1 min-w-0">
            <TimelineStep step={s} index={i} className="flex-1" />
            {i < steps.length - 1 && (
              <div className="flex items-center px-4 shrink-0">
                <ArrowRight className="h-5 w-5 text-accent" aria-hidden />
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

/* ---- Cartes de domaines / services ---- */
export function FeatureGrid({ items }) {
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {items.map((it) => (
        <Card key={it.title} className="rounded-2xl border-border em-card-hover">
          <CardContent className="p-6">
            <h3 className="font-display text-lg font-bold text-primary">{it.title}</h3>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{it.text}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ---- Maillage interne (liens vers d'autres pages) ---- */
export function RelatedLinks({ links }) {
  if (!links || !links.length) return null;
  return (
    <div className="flex flex-wrap gap-3">
      {links.map((l) => (
        <Link key={l.to} to={l.to} className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-sm text-primary hover:bg-secondary transition-colors">
          {l.label} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      ))}
    </div>
  );
}

/* ---- Bloc de conversion final ---- */
export function FinalCTA({ title = "Prêt à y voir plus clair sur votre énergie ?", text = "Demandez votre étude gratuite ou déposez votre facture. Sans engagement, partout en France.", className = "", cmsKey = "" }) {
  const c = useSiteContent(cmsKey ? [`v2.${cmsKey}.title`, `v2.${cmsKey}.text`] : []);
  if (cmsKey) {
    title = c(`v2.${cmsKey}.title`, title);
    text = c(`v2.${cmsKey}.text`, text);
  }
  return (
    <section className={`em-container pt-10 md:pt-12 pb-12 md:pb-16 ${className}`}>
      <div className="bg-primary rounded-3xl text-primary-foreground px-8 py-12 md:px-16 md:py-14 flex flex-col gap-8 md:flex-row md:items-center md:justify-between md:gap-10">
        <div className="md:flex-1">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{title}</h2>
          <p className="text-primary-foreground/80 mt-4 max-w-xl">{text}</p>
        </div>
        <div className="shrink-0">
          <div className="group relative">
            {/* Facture qui descend dans le bouton */}
            <span aria-hidden className="pointer-events-none absolute bottom-full left-1/2 z-0 mb-1 -translate-x-1/2">
              <span className="em-bill block h-12 w-9 rounded-[5px] bg-white shadow-xl ring-1 ring-black/5">
                <span className="mx-1.5 mt-2 block h-[3px] rounded bg-primary/45" />
                <span className="mx-1.5 mt-[5px] block h-[3px] rounded bg-primary/45" />
                <span className="mx-1.5 mt-[5px] block h-[3px] w-4 rounded bg-primary/45" />
                <span className="mx-1.5 mt-[5px] block h-[3px] rounded bg-primary/30" />
              </span>
            </span>
            <Link
              to="/contact?type=facture"
              className="relative z-10 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-white px-7 font-bold text-primary shadow-lg transition-colors hover:bg-white/90"
            >
              <FileText className="h-4 w-4" aria-hidden /> Déposer ma facture
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
